import { useEffect } from "react";

interface SEOMeta {
  title: string;
  description: string;
  keywords?: string;
  canonical?: string;
  ogTitle?: string;
  ogDescription?: string;
  ogUrl?: string;
  ogType?: string;
  twitterCard?: string;
  jsonLd?: Record<string, unknown> | Record<string, unknown>[];
}

export default function SEOHead({ meta }: { meta: SEOMeta }) {
  useEffect(() => {
    const prevTitle = document.title;
    document.title = meta.title;

    const setMetaTag = (name: string, content: string, property = false) => {
      const attr = property ? "property" : "name";
      let el = document.querySelector(`meta[${attr}="${name}"]`) as HTMLMetaElement | null;
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute(attr, name);
        document.head.appendChild(el);
      }
      el.setAttribute("content", content);
    };

    setMetaTag("description", meta.description);
    if (meta.keywords) setMetaTag("keywords", meta.keywords);

    if (meta.ogTitle) setMetaTag("og:title", meta.ogTitle, true);
    if (meta.ogDescription) setMetaTag("og:description", meta.ogDescription, true);
    if (meta.ogUrl) setMetaTag("og:url", meta.ogUrl, true);
    setMetaTag("og:type", meta.ogType || "website", true);
    setMetaTag("og:locale", "zh_CN", true);

    setMetaTag("twitter:card", meta.twitterCard || "summary_large_image");
    if (meta.ogTitle) setMetaTag("twitter:title", meta.ogTitle);
    if (meta.ogDescription) setMetaTag("twitter:description", meta.ogDescription);

    if (meta.canonical) {
      let link = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
      if (!link) {
        link = document.createElement("link");
        link.setAttribute("rel", "canonical");
        document.head.appendChild(link);
      }
      link.setAttribute("href", meta.canonical);
    }

    let scriptEl: HTMLScriptElement | null = null;
    if (meta.jsonLd) {
      const existingScripts = document.querySelectorAll('script[type="application/ld+json"]');
      existingScripts.forEach((s) => s.remove());
      scriptEl = document.createElement("script");
      scriptEl.type = "application/ld+json";
      scriptEl.textContent = JSON.stringify(meta.jsonLd);
      document.head.appendChild(scriptEl);
    }

    return () => {
      document.title = prevTitle;
      if (scriptEl) scriptEl.remove();
    };
  }, [meta]);

  return null;
}